/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  BaseBlockSnapshot,
  BlockType,
  FileBlockSnapshot,
  FileStatus,
} from "@vienna/common/src/entities/block-snapshot.entity";

import { BaseBlock } from "src/classes/block/BaseBlock";
import { FileHandlingMethods } from "src/classes/block/block.interfaces";
import { PlateSocket } from "src/classes/plate/PlateSocket/PlateSocket";
import { FileUtils } from "src/classes/utilities/FileUtils";
export enum FileType {
  Audio,
  Video,
  PDF,
  Other,
}

export class FileBlock extends BaseBlock<FileBlockSnapshot> implements FileHandlingMethods {
  private static supportedAudioExtensions = ["mp3", "wav", "ogg", "m4a", "aac", "flac"];

  private static supportedVideoExtensions = ["mp4", "m4v", "ogv", "wmv", "avi", "mov", "mpg", "mpeg", "flv"];

  private static fileBucketName = `${process.env.ENVIRONMENT}-vht-file-bucket`;

  private static fileBucketURL = `https://${FileBlock.fileBucketName}.s3.amazonaws.com`;

  constructor(snapshot: FileBlockSnapshot, plateSocket: PlateSocket | null) {
    super(snapshot, plateSocket);
  }

  /* -------------------------------------------------- */
  /*                      Actions                       */
  /* -------------------------------------------------- */

  /* -------------------------------------------------- */
  /*                      File URL                      */
  /* -------------------------------------------------- */

  public get fileURL(): string {
    return `${FileBlock.fileBucketURL}/${this.fileURIComponent}`;
  }

  public get pdfEmbedURL(): string {
    if (!this.isPDF) return "";
    return `${this.fileURL}#toolbar=0&view=Fit`;
  }

  private get fileURIComponent(): string {
    return encodeURIComponent(this.snapshot.filename!);
  }

  /* -------------------------------------------------- */
  /*                File Upload Tracker                 */
  /* -------------------------------------------------- */
  public addFile(file: File): void {
    if (!file) return;
    if (!this.plateSocket) return;
    const renamedFile = FileUtils.renameFile(file, this.snapshot.id);
    this.setState({
      filename: renamedFile.name,
      originalFilename: file.name,
      filesize: renamedFile.size,
      fileStatus: FileStatus.Uploading,
    });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
    FileUtils.uploadFileWithStreaming(renamedFile, FileBlock.fileBucketName, this.monitorUploadProgress);
  }

  /* ------------------ Must be arrow function to be bound as streaming callback ------------------ */
  public monitorUploadProgress = (fileStatus: FileStatus, uploadPercent?: number): void => {
    if (!this.plateSocket) return;
    this.setState({ fileStatus, uploadPercent: uploadPercent || this.snapshot.uploadPercent });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  };

  /* -------------------------------------------------- */
  /*                     File Type                      */
  /* -------------------------------------------------- */
  public get fileType(): FileType {
    if (this.isAudio) return FileType.Audio;
    if (this.isVideo) return FileType.Video;
    if (this.isPDF) return FileType.PDF;
    return FileType.Other;
  }

  private get isAudio(): boolean {
    return FileBlock.supportedAudioExtensions.includes(this.extension);
  }

  public get isVideo(): boolean {
    return FileBlock.supportedVideoExtensions.includes(this.extension);
  }

  public get isPDF(): boolean {
    return this.extension === "pdf";
  }

  private get extension(): string {
    if (!this.snapshot.filename) return "";
    if (!this.snapshot.originalFilename) return "";
    const filename = this.snapshot.filename;
    const originalFilename = this.snapshot.originalFilename;
    const extension = filename.split(".").pop() || null;
    const fallbackExtension = originalFilename.split(".").pop() || null;
    if (!extension && !fallbackExtension) {
      throw new Error(`The file with filename ${this.snapshot.filename} has no extension, which is a problem.`);
    }
    return extension || fallbackExtension || "";
  }

  /* -------------------------------------------------- */
  /*                     File Name                      */
  /* -------------------------------------------------- */
  public get fileDisplayName(): string {
    if (!this.snapshot.originalFilename) return "";
    return this.snapshot.originalFilename.split(".").slice(0, -1).join(".") || this.snapshot.originalFilename;
  }

  /* -------------------------------------------------- */
  /*                      File Size                     */
  /* -------------------------------------------------- */
  public get readableFileSize(): string {
    return FileUtils.getReadableFileSizeFromBytes(this.snapshot.filesize || 0);
  }

  /* -------------------------------------------------- */
  /*                      Create                        */
  /* -------------------------------------------------- */
  public static newSnapshot(baseSnapshot: BaseBlockSnapshot): FileBlockSnapshot {
    const snapshot: FileBlockSnapshot = {
      ...baseSnapshot,
      type: BlockType.File,
      filename: null,
      filesize: null,
      originalFilename: null,
      fileStatus: FileStatus.None,
      uploadPercent: 0,
    };
    return snapshot;
  }
}
